/**
 * Shared rendering for verify-readiness blocker/warning chips.
 *
 * A chip is the small clickable card that represents one entry from
 * `GET /api/batches/:id/blockers`. Hovering shows the full `detail` text via
 * a `title` attribute. Clicking jumps to and briefly highlights the source
 * row (raw row for UNMATCHED_ROW; settlement row for the other two).
 */
import type { VerifyBlocker } from '../api';

export function kindLabel(kind: VerifyBlocker['kind']): string {
  switch (kind) {
    case 'UNMATCHED_ROW':
      return 'Unmatched row';
    case 'PENDING_CONFIRMATION':
      return 'Missing value';
    case 'MAJOR_VARIANCE':
      return 'Major variance';
  }
}

export function sourceIdOf(b: VerifyBlocker): number {
  return b.kind === 'UNMATCHED_ROW' ? b.rawRowId : b.settlementId;
}

export function targetKindOf(b: VerifyBlocker): 'raw-row' | 'settlement' {
  return b.kind === 'UNMATCHED_ROW' ? 'raw-row' : 'settlement';
}

export function BlockerChip({
  blocker,
  onClick,
}: {
  blocker: VerifyBlocker;
  onClick: () => void;
}) {
  const isBlock = blocker.severity === 'block';
  const cls = isBlock
    ? 'border-neg/25 app-shell-surface-soft hover:bg-surface-raised'
    : 'border-amber-300/55 app-shell-surface-soft hover:bg-surface-raised';
  return (
    <button
      type="button"
      onClick={onClick}
      title={blocker.detail}
      className={`flex w-full min-w-0 max-w-full items-start gap-2 rounded-md border px-2.5 py-1.5 text-left text-caption transition-colors sm:w-auto ${cls}`}
    >
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
          isBlock ? "bg-neg/10 text-neg" : "bg-amber-100 text-amber-800"
        }`}
      >
        {kindLabel(blocker.kind)}
      </span>
      <span className="min-w-0 flex-1 whitespace-normal break-words text-ink-muted">{blocker.detail}</span>
    </button>
  );
}
